import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Go Damn — "You might also like" inside the cart drawer.
 *
 * Pulls Shopify's own product recommendations for the first item in the cart and
 * renders them through the `gd-cart-recommendations` section. Adding from here
 * posts to the cart and lets the theme morph the drawer, so the line items,
 * totals and cart bubble all stay in sync.
 *
 * The theme re-renders (morphs) the drawer after every cart change, which wipes
 * anything injected here. Rather than racing that, we keep the fetched markup
 * and re-apply it whenever the mount point turns up empty.
 *
 * The loading skeleton is pure CSS, keyed off whether the mount has content
 * (`:empty`) — not a JS/attribute flag. A section morph can land after our
 * debounced reload and stomp any attribute we set, but it can't desync from
 * the mount's actual children, so the skeleton never gets stuck showing.
 * `hidden` on the host is only for the terminal "no recommendations" case.
 */
class GdCartRecs extends HTMLElement {
  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number | undefined} */
  #reloadTimer;

  /** @type {string} Markup of the last successful fetch. */
  #html = '';

  /** @type {string} product id + limit the cached markup belongs to. */
  #key = '';

  /** @type {string} Key of the request currently in flight, if any — guards against firing a duplicate. */
  #pendingKey = '';

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleReload);

    this.#observer = new MutationObserver(this.#render);
    this.#observer.observe(this, { childList: true, subtree: true });

    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#scheduleReload);
    this.#observer?.disconnect();
    this.#observer = null;
    this.#fetchController?.abort();
    clearTimeout(this.#reloadTimer);
  }

  get #mount() {
    return this.querySelector('[data-recs-mount]');
  }

  get #currentKey() {
    const limit = parseInt(this.dataset.limit ?? '6', 10) || 6;
    return `${this.dataset.productId ?? ''}:${limit}`;
  }

  /** Puts the cached markup back whenever the mount is empty. */
  #render = () => {
    const mount = this.#mount;
    if (!mount || !this.#html) return;
    if (mount.children.length > 0) return;

    mount.innerHTML = this.#html;
    this.hidden = false;
  };

  #scheduleReload = () => {
    clearTimeout(this.#reloadTimer);
    this.#reloadTimer = setTimeout(() => this.#load(), 250);
  };

  /**
   * @param {number} [attempt] - The element can be upgraded before the browser
   *   has parsed its children, so retry briefly before giving up on the mount.
   */
  async #load(attempt = 0) {
    const mount = this.#mount;

    if (!mount) {
      if (attempt < 10) {
        setTimeout(() => this.#load(attempt + 1), 50);
      } else {
        // The mount never showed up — don't leave the skeleton spinning forever.
        this.hidden = true;
      }
      return;
    }

    const productId = this.dataset.productId;
    if (!productId) {
      this.hidden = true;
      return;
    }

    const key = this.#currentKey;

    // Same products as last time: just make sure they are on screen.
    if (key === this.#key && this.#html) {
      this.#render();
      return;
    }

    // A request for this exact key is already in flight — don't fire a duplicate.
    if (this.#fetchController && key === this.#pendingKey) return;

    const limit = parseInt(this.dataset.limit ?? '6', 10) || 6;
    const root = window.Shopify?.routes?.root ?? '/';
    const url = `${root}recommendations/products?section_id=gd-cart-recommendations&product_id=${encodeURIComponent(
      productId
    )}&limit=${limit}`;

    this.#fetchController?.abort();
    const controller = new AbortController();
    this.#fetchController = controller;
    this.#pendingKey = key;

    // A stalled request (dead network, slow endpoint) shouldn't leave the
    // skeleton showing indefinitely — give up gracefully after a few seconds.
    const timeout = setTimeout(() => controller.abort(), 6000);

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

      const html = await response.text();
      const track = new DOMParser().parseFromString(html, 'text/html').querySelector('.gd-recs__track');

      if (!track || track.children.length === 0) {
        this.#html = '';
        this.#key = key;
        this.hidden = true;
        return;
      }

      this.#html = track.innerHTML;
      this.#key = key;

      const target = this.#mount;
      if (target) {
        target.innerHTML = this.#html;
        this.hidden = false;
      }
    } catch (error) {
      // A newer call already superseded this one — let it own the UI state
      // instead of a stale abort (or timeout) hiding what it just rendered.
      if (this.#fetchController !== controller) return;
      console.error(error);
      this.hidden = true;
    } finally {
      clearTimeout(timeout);
      if (this.#fetchController === controller) {
        this.#fetchController = null;
        this.#pendingKey = '';
      }
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-gd-add]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.#addToCart(button);
  };

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    const variantId = Number(button.dataset.gdAdd);
    const sectionId = this.dataset.sectionId;
    if (!variantId || !sectionId) return;

    const label = button.querySelector('[data-gd-add-label]');
    const icon = button.querySelector('[data-gd-add-icon]');
    const originalLabel = button.dataset.gdLabel ?? label?.textContent ?? '';

    button.setAttribute('aria-busy', 'true');

    try {
      const addUrl = typeof Theme !== 'undefined' ? Theme.routes?.cart_add_url : null;

      const response = await fetch(addUrl ?? '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');

      const addedLabel = typeof Theme !== 'undefined' ? Theme.translations?.added : null;
      if (label) label.textContent = addedLabel ?? 'Added';
      if (icon) icon.textContent = '✓';
      button.classList.add('is-added');

      document.dispatchEvent(
        new CartAddEvent(data, 'gd-cart-recs', {
          source: 'gd-cart-recs',
          variantId: String(variantId),
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      if (label) label.textContent = originalLabel;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('gd-cart-recs')) {
  customElements.define('gd-cart-recs', GdCartRecs);
}
